import { useId } from 'react';
import { Checkbox } from '@/design-system';
import type { AnswerValue, Field } from '@/lib/intake/catalogue';
import type { Issue } from '@/lib/intake/rules';
import { COPY } from '../copy';
import { describedBy } from './describedBy';
import { GroupedField, LabelledField } from './FieldFrame';
import styles from './fields.module.css';

export interface QuestionFieldProps {
  field: Field;
  value: AnswerValue | undefined;
  required: boolean;
  /** Shown only once the person has left the field, or it arrived with a value. */
  issues: readonly Issue[];
  maxLength: number;
  disabled: boolean;
  onChange: (value: AnswerValue | null) => void;
  onBlur: () => void;
}

/**
 * One question, drawn from its catalogue entry. Six types, one component each
 * underneath, so a new question is a catalogue change and never a page change.
 */
export function QuestionField(props: QuestionFieldProps) {
  const id = `q-${props.field.id}-${useId().replace(/:/g, '')}`;
  switch (props.field.type) {
    case 'textarea':
      return <TextAreaField {...props} id={id} />;
    case 'select':
      return <SelectField {...props} id={id} />;
    case 'yesno':
      return <YesNoField {...props} id={id} />;
    case 'multi':
      return <MultiField {...props} id={id} />;
    case 'date':
      return <InputField {...props} id={id} type="date" />;
    default:
      return (
        <InputField
          {...props}
          id={id}
          type={props.field.format === 'email' ? 'email' : props.field.format === 'phone' ? 'tel' : 'text'}
        />
      );
  }
}

type Props = QuestionFieldProps & { id: string };

function text(value: AnswerValue | undefined): string {
  return typeof value === 'string' ? value : '';
}

function frame(props: Props) {
  return {
    id: props.id,
    label: props.field.label,
    required: props.required,
    hint: props.field.hint,
    issues: props.issues,
  };
}

function InputField(props: Props & { type: 'text' | 'email' | 'tel' | 'date' }) {
  const invalid = props.issues.some((i) => i.severity === 'ERROR');
  const autoComplete = props.field.format === 'email' ? 'email' : props.field.format === 'phone' ? 'tel' : 'off';
  return (
    <LabelledField {...frame(props)}>
      <input
        id={props.id}
        type={props.type}
        className={[styles.control, invalid ? styles.invalid : ''].join(' ')}
        value={text(props.value)}
        maxLength={props.type === 'date' ? undefined : props.maxLength}
        autoComplete={autoComplete}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy(props.id, props.field.hint, props.issues)}
        disabled={props.disabled}
        onChange={(event) => props.onChange(event.target.value)}
        onBlur={props.onBlur}
      />
    </LabelledField>
  );
}

function TextAreaField(props: Props) {
  return (
    <LabelledField {...frame(props)}>
      <textarea
        id={props.id}
        className={[styles.control, styles.textarea].join(' ')}
        value={text(props.value)}
        maxLength={props.maxLength}
        rows={4}
        aria-describedby={describedBy(props.id, props.field.hint, props.issues)}
        disabled={props.disabled}
        onChange={(event) => props.onChange(event.target.value)}
        onBlur={props.onBlur}
      />
    </LabelledField>
  );
}

function SelectField(props: Props) {
  return (
    <LabelledField {...frame(props)}>
      <select
        id={props.id}
        className={styles.control}
        value={text(props.value)}
        aria-describedby={describedBy(props.id, props.field.hint, props.issues)}
        disabled={props.disabled}
        onChange={(event) => props.onChange(event.target.value)}
        onBlur={props.onBlur}
      >
        <option value="">{COPY.chooseOne}</option>
        {(props.field.options ?? []).map((option) => (
          <option key={option.key} value={option.key}>
            {option.label}
          </option>
        ))}
      </select>
    </LabelledField>
  );
}

/** A radio group, so the choice is announced and arrow keys move between Yes and No. */
function YesNoField(props: Props) {
  const choices = [
    { value: 'yes', label: COPY.yes },
    { value: 'no', label: COPY.no },
  ];
  return (
    <GroupedField {...frame(props)}>
      <div className={styles.pills}>
        {choices.map((choice) => (
          <label key={choice.value} className={styles.pill}>
            <input
              type="radio"
              name={props.id}
              value={choice.value}
              checked={props.value === choice.value}
              disabled={props.disabled}
              onChange={() => props.onChange(choice.value)}
              onBlur={props.onBlur}
            />
            {choice.label}
          </label>
        ))}
      </div>
    </GroupedField>
  );
}

function MultiField(props: Props) {
  const ticked = Array.isArray(props.value) ? props.value : [];
  const options = props.field.options ?? [];
  function toggle(key: string, on: boolean) {
    // Kept in catalogue order, so the same choices always save the same list.
    const next = options.map((o) => o.key).filter((k) => (k === key ? on : ticked.includes(k)));
    props.onChange(next);
  }
  return (
    <GroupedField {...frame(props)}>
      <div className={styles.options}>
        {options.map((option) => (
          <div key={option.key} className={styles.option}>
            <Checkbox
              label={option.label}
              checked={ticked.includes(option.key)}
              disabled={props.disabled}
              onChange={(event) => toggle(option.key, event.target.checked)}
              onBlur={props.onBlur}
            />
          </div>
        ))}
      </div>
    </GroupedField>
  );
}
